/**
 * CollapsibleSection — wraps any section. Renders as an always-open
 * block on desktop (>=md); on mobile (<md), renders as a native
 * <details> element so the section can be collapsed.
 *
 * Implementation: single render of children inside <details>, with
 * `open` controlled by React state. On mount and on viewport-width
 * change, we set `open=true` when md+ is matched. Mobile users can
 * still toggle via the summary; their toggle is preserved because we
 * only force `open=true` (never force closed) based on viewport.
 *
 * Why not pure-CSS: the original approach (md:!block override of the
 * UA `details:not([open]) > *:not(summary) { display:none }`) DOES
 * compute the child as display:block on desktop, but native <details>
 * still collapses the child's layout box to 0 height when the element
 * is in the closed state. The override is layout-ineffective. A small
 * matchMedia effect is the simplest reliable fix.
 *
 * Per spec §7.2, every section after the Audience block uses this on
 * mobile. Sections above-the-fold do NOT — they stay always-open to
 * preserve the quick-glance UX.
 */
import { ReactNode, useEffect, useState } from 'react';

interface CollapsibleSectionProps {
  title: string;
  /** Set to false to skip rendering entirely (matches the section's own
   * null-render decision). */
  present?: boolean;
  defaultOpen?: boolean;
  children: ReactNode;
}

const DESKTOP_MQ = '(min-width: 768px)';

function initialOpen(defaultOpen: boolean): boolean {
  // Initialize from matchMedia synchronously to avoid a closed-then-open
  // flicker on desktop mount. SSR-safe (returns defaultOpen if window is
  // undefined). On desktop we always want open; on mobile honour the
  // caller's defaultOpen.
  if (typeof window === 'undefined') return defaultOpen;
  return window.matchMedia(DESKTOP_MQ).matches || defaultOpen;
}

export function CollapsibleSection({
  title,
  present = true,
  defaultOpen = false,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState<boolean>(() => initialOpen(defaultOpen));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(DESKTOP_MQ);
    // On viewport widening into desktop range, force-open (so collapsed
    // mobile state doesn't persist invisibly when the user resizes up).
    // On narrowing into mobile range, do NOT force-close — the user may
    // have explicitly opened it.
    const handler = () => {
      if (mq.matches) setOpen(true);
    };
    handler();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Presence gate sits below the hooks so hook call order stays stable across
  // renders (Rules of Hooks); the hooks above don't depend on `present`.
  if (!present) return null;

  return (
    <details
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      className="block mb-4 group/cs"
    >
      <summary className="md:hidden cursor-pointer list-none p-3 mb-2 rounded-2xl border border-border bg-card text-body-sm-semibold text-foreground flex items-center justify-between">
        <span>{title}</span>
        <span className="text-muted-foreground text-caption-regular group-open/cs:hidden">tap to expand</span>
        <span className="text-muted-foreground text-caption-regular hidden group-open/cs:inline">tap to collapse</span>
      </summary>
      <div>{children}</div>
    </details>
  );
}
